import { useState } from "react";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Download, ShieldAlert, Plus, Trash2, CheckCircle2, Loader2 } from "lucide-react";

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  const head = lines.shift().split(",").map((h) => h.trim().toLowerCase());
  const idx = (k) => head.findIndex((h) => h.includes(k));
  const iId = idx("id"), iQty = idx("qty") >= 0 ? idx("qty") : idx("quantity"), iUnit = idx("unit"), iName = idx("name");
  return lines.filter(Boolean).map((ln) => { const c = ln.split(","); return { itemId: (c[iId] || "").trim(), quantity: (c[iQty] || "").trim(), unit: (c[iUnit] || "").trim(), name: (c[iName] || "").trim() }; });
}

export default function HistoricalStock() {
  const { data: listRaw, refetch } = useResource("/historical-stock");
  const list = Array.isArray(listRaw) ? listRaw : [];
  const [asOfDate, setAsOfDate] = useState("");
  const [lines, setLines] = useState([{ itemId: "", quantity: "", unit: "" }]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const onFile = async (f) => { if (!f) return; const txt = await f.text(); setLines(parseCsv(txt)); toast.success("CSV parsed — click Preview"); };

  const doPreview = async () => {
    if (!asOfDate) return toast.error("Select an as-of date");
    setBusy(true);
    try { const r = await api.post("/historical-stock/preview", { asOfDate, lines }); setPreview(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); } finally { setBusy(false); }
  };
  const saveDraft = async () => {
    setBusy(true);
    try { const r = await api.post("/historical-stock", { asOfDate, lines }); toast.success(`Draft ${r.data.reconstructionId} saved`); setActive(r.data); refetch(); }
    catch (e) { toast.error(apiErrorMessage(e, "Save failed")); } finally { setBusy(false); }
  };
  const approve = async (rid) => {
    setBusy(true);
    try { await api.post(`/historical-stock/${rid}/approve`, {}); toast.success("Approved — ready to post"); refetch(); }
    catch (e) { toast.error(apiErrorMessage(e, "Approve failed")); } finally { setBusy(false); }
  };
  const postToLedger = async (rid) => {
    if (!window.confirm("This will create opening-balance ledger entries. It will NOT create purchase bills, GRNs, sales or dispatches. Continue?")) return;
    setBusy(true);
    try { const r = await api.post(`/historical-stock/${rid}/post`, {}); toast.success(r.data.idempotentReplay ? "Already posted" : `Posted ${r.data.postedLines} opening entries`); refetch(); }
    catch (e) { toast.error(apiErrorMessage(e, "Post failed")); } finally { setBusy(false); }
  };
  const dlReport = async (rid, fmt) => {
    try { const res = await api.get(`/historical-stock/${rid}/report?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement("a"); a.href = url; a.download = `historical-stock.${fmt === "excel" ? "xlsx" : fmt}`; a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Download failed")); }
  };

  const S = preview?.summary || {};
  return (
    <div className="space-y-6">
      <PageHeader title="Historical Stock Reconstruction" subtitle="Super Admin only · Reconstruct opening stock as of a date. Never modifies live inventory." />
      <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50/70 px-4 py-3 text-sm text-rose-900">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /><span>High-risk workflow. Draft → Review → Approve. Approval records reconstruction data for reporting only and posts <strong>NOTHING</strong> to the live inventory ledger.</span>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">As-of Date</Label><Input type="date" data-testid="hs-date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} className="w-44" /></div>
          <label className="flex h-10 cursor-pointer items-center gap-2 rounded-md border border-dashed border-stone-300 px-3 text-sm text-stone-600 hover:border-amber-400">
            <input type="file" accept=".csv" className="hidden" data-testid="hs-file" onChange={(e) => onFile(e.target.files?.[0])} /> Import CSV (id, quantity, unit)
          </label>
        </div>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`hs-line-${i}`}>
              <Input placeholder="Ingredient/Product ID (ING-/PROD-)" data-testid={`hs-id-${i}`} value={l.itemId} onChange={(e) => setLine(i, { itemId: e.target.value })} className="h-9 w-64" />
              <Input placeholder="Qty" type="number" data-testid={`hs-qty-${i}`} value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className="h-9 w-28" />
              <Input placeholder="Unit" data-testid={`hs-unit-${i}`} value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} className="h-9 w-24" />
              <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="gap-1" data-testid="hs-add" onClick={() => setLines((ls) => [...ls, { itemId: "", quantity: "", unit: "" }])}><Plus className="h-3 w-3" /> Add line</Button>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" data-testid="hs-preview" disabled={busy} onClick={doPreview}>Preview</Button>
          <Button className="bg-stone-800 hover:bg-stone-900" data-testid="hs-savedraft" disabled={busy} onClick={saveDraft}>{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save Draft</Button>
        </div>
      </div>

      {preview && (
        <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="hs-preview-panel">
          <div className="mb-2 grid grid-cols-2 gap-2 text-center sm:grid-cols-5">
            {[["Total", S.total], ["Valid", S.valid], ["Invalid", S.invalid], ["Missing ID", S.missingId], ["Duplicate", S.duplicate]].map(([k, v]) => (
              <div key={k} className="rounded border border-stone-200 bg-stone-50/60 px-2 py-1"><div className="font-mono text-base font-bold">{v}</div><div className="text-[10px] uppercase text-stone-500">{k}</div></div>
            ))}
          </div>
          <table className="w-full text-xs"><thead className="bg-stone-50 text-left uppercase text-stone-500"><tr>{["Item", "ID", "Unit", "Historical", "Current Live", "Difference", "Status"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-stone-100">
              {preview.rows.map((r, i) => (
                <tr key={i}><td className="px-2 py-1.5">{r.name}</td><td className="px-2 py-1.5 font-mono">{r.itemId}</td><td className="px-2 py-1.5">{r.unit}</td>
                  <td className="px-2 py-1.5 text-right">{r.quantity ?? "—"}</td><td className="px-2 py-1.5 text-right">{r.currentLiveQty}</td>
                  <td className="px-2 py-1.5 text-right">{r.difference ?? "—"}</td>
                  <td className="px-2 py-1.5"><span className={r.status === "NEW" || r.status === "UPDATE" ? "text-emerald-600" : "text-rose-600"}>{r.status}</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="hs-list">
        <div className="mb-2 text-sm font-semibold text-stone-800">Reconstructions</div>
        {list.length === 0 ? <p className="text-sm text-stone-400">None yet.</p> : (
          <table className="w-full text-sm"><thead className="text-left text-[10px] uppercase text-stone-500"><tr><th className="py-1.5">ID</th><th>As of</th><th>Status</th><th>Lines</th><th>By</th><th></th></tr></thead>
            <tbody className="divide-y divide-stone-100">
              {list.map((r) => (
                <tr key={r.id} data-testid={`hs-rec-${r.id}`}>
                  <td className="py-2 font-mono text-xs">{r.reconstructionId}</td><td>{r.asOfDate}</td>
                  <td><span className={r.status === "APPROVED" ? "text-emerald-600" : "text-amber-700"}>{r.status}</span></td>
                  <td>{(r.lines || []).length}</td><td className="text-stone-500">{r.createdBy}</td>
                  <td className="flex gap-1 py-1.5">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => dlReport(r.id, "pdf")}>PDF</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => dlReport(r.id, "excel")}>Excel</Button>
                    {r.status === "DRAFT" && <Button size="sm" className="h-7 gap-1 bg-emerald-600 px-2 text-[11px] hover:bg-emerald-700" data-testid={`hs-approve-${r.id}`} onClick={() => approve(r.id)}><CheckCircle2 className="h-3 w-3" /> Approve</Button>}
                    {r.status === "APPROVED" && <Button size="sm" className="h-7 gap-1 bg-amber-700 px-2 text-[11px] hover:bg-amber-800" data-testid={`hs-post-${r.id}`} onClick={() => postToLedger(r.id)}>Post to Ledger</Button>}
                    {r.status === "POSTED" && <span className="text-[11px] font-semibold text-emerald-700">✓ Posted</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
